(function () {
  'use strict';
  const KEY = 'wam_pending_generation_v1';
  const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const messages = {
    INVALID_INPUT: 'Choose a photo or enter the text for your design.',
    INVALID_IMAGE: 'Please choose a PNG, JPG or WebP image.',
    FILE_TOO_LARGE: 'This photo is too large. Choose a file under 10MB.',
    QUEUE_FULL: 'We are working on several designs. Please try again shortly.',
    RATE_LIMITED: 'You have reached the design limit. Please try again later.',
    EMAIL_REQUIRED: 'Add your email to save another version.',
    EMAIL_CHANGE_LIMIT: 'This design has reached its email change limit. You can still save the image below.',
    GENERATION_UNCERTAIN: 'We could not confirm this preview. Your earlier designs are still saved.',
    GENERATION_FAILED: 'We could not finish this preview. Your earlier designs are still saved.',
    UPLOAD_NOT_RECEIVED: 'Your upload was not received. Choose the photo again to start.',
    JOB_NOT_FOUND: 'This pending preview is no longer available. Your saved designs are below.'
  };
  function create(options) {
    let current = null;
    let accepted = false;
    let stopped = false;
    let pageSuspended = false;
    let inMemoryPayload = null;
    let payloadReleased = false;
    const activeRequests = new Set();
    window.addEventListener('pagehide', () => { pageSuspended = true; activeRequests.forEach(request => request.abort()); });
    window.addEventListener('pageshow', () => { pageSuspended = false; });
    const track = (name, data) => { try { options.track(name, data); } catch (_) {} };
    const notify = state => {
      accepted = true;
      const localState = {...state};
      if (Number.isFinite(state.elapsedMs)) localState.createdAt = Date.now() - state.elapsedMs;
      if (current && Number.isFinite(state.remainingMs)) current.expiresAt = Date.now() + state.remainingMs;
      if (current && !payloadReleased) {
        current.accepted = true; persist(); payloadReleased = true; inMemoryPayload = null;
        void payloadStore('delete', null, current.id);
      }
      options.onState?.(localState);
    };
    function persist() {
      try { localStorage.setItem(KEY, JSON.stringify(current)); return true; }
      catch (_) { track('wam_kurtarma_depolama_hata', {}); return false; }
    }
    function payloadStore(action, value, id = current?.id) {
      if (!id || !window.indexedDB) return Promise.resolve(null);
      return new Promise(resolve => {
        let db, transaction, settled = false, result = null;
        const finish = value => { if (settled) return; settled = true; clearTimeout(timer); db?.close(); resolve(value); };
        const timer = setTimeout(() => { try { transaction?.abort(); } catch (_) {} finish(null); }, 4000);
        let opening;
        try { opening = indexedDB.open('wam-generation-payload-v1', 1); } catch (_) { finish(null); return; }
        opening.onupgradeneeded = () => { opening.result.createObjectStore('payloads', {keyPath:'id'}); };
        opening.onerror = () => finish(null);
        opening.onsuccess = () => {
          db = opening.result; if (settled) { db.close(); return; }
          try {
            transaction = db.transaction('payloads', 'readwrite'); const records = transaction.objectStore('payloads');
            const request = action === 'put' ? records.put(value) : action === 'delete' ? records.delete(id) : records.get(id);
            request.onsuccess = () => { result = action === 'get' ? request.result : true; };
            const cursor = records.openCursor();
            cursor.onsuccess = () => { const item = cursor.result; if (!item) return; if (item.value.expiresAt <= Date.now()) item.delete(); item.continue(); };
            transaction.oncomplete = () => finish(result);
            transaction.onerror = transaction.onabort = () => finish(null);
          } catch (_) { finish(null); }
        };
      });
    }
    function clear(id = current?.id) { try { localStorage.removeItem(KEY); } catch (_) {} if (id) void payloadStore('delete', null, id); }

    function read() {
      try {
        const value = JSON.parse(localStorage.getItem(KEY) || 'null');
        if (!value || !UUID.test(value.id) || !/^[a-f0-9]{64}$/.test(value.token) || !Number.isFinite(value.expiresAt)) return null;
        if (value.expiresAt <= Date.now()) { clear(value.id); track('wam_bekleyen_sure_doldu', { istek_id: value.id }); return null; }
        return value;
      } catch (_) { return null; }
    }
    function privacy() {
      const ph = window.posthog;
      if (!ph?.set_config) return;
      const recording = ph.config?.session_recording || {};
      if (recording.wamGenerationMasked) return;
      const previous = recording.maskCapturedNetworkRequestFn;
      const running = ph.sessionRecordingStarted?.() === true;
      if (running) ph.stopSessionRecording();
      ph.set_config({ session_recording: { ...recording, wamGenerationMasked: true,
        maskCapturedNetworkRequestFn: request => {
          if (/\/(?:generation-jobs|detect-category|email-designs)(?:[/?]|$)/.test(request?.name || '')) return null;
          return previous ? previous(request) : request;
        }
      } });
      if (running) ph.startSessionRecording();
    }
    privacy();
    async function request(path, init) {
      privacy();
      const controller = new AbortController(); activeRequests.add(controller);
      const timer = setTimeout(() => controller.abort(), 25_000);
      try {
        const response = await fetch(options.api + '/api/generation-jobs' + path, {
          ...init, headers: { ...(init?.headers || {}), Authorization: 'Bearer ' + current.token }, signal: controller.signal
        });
        const data = await response.json().catch(() => ({}));
        return { response, data };
      } finally { clearTimeout(timer); activeRequests.delete(controller); }
    }

    function error(code) {
      const err = new Error(messages[code] || 'We could not load this preview. Please try again.'); err.code = code; return err;
    }
    async function wait(resuming) {
      let missingSince = 0; let uploadRetries = 0;
      while (!stopped && current && Date.now() < current.expiresAt) {
        if (pageSuspended) await new Promise(resolve => window.addEventListener('pageshow', resolve, { once: true }));
        let reply;
        try { reply = await request('/' + current.id); }
        catch (_) { options.onState?.({ stage: accepted ? 'offline' : 'upload_check', createdAt: current.createdAt }); }
        if (reply) {
          const { response, data } = reply;
          if (response.ok) {
            missingSince = 0; notify(data);
            if (data.status === 'ready' && data.result?.preview_url) {
              if (resuming) track('wam_tasarim_kurtarildi', { istek_id: current.id, tasarim_id: data.result.design_id, kaynak: 'yenileme' });
              return data.result;
            }
            if (data.status === 'failed' || data.status === 'uncertain') { clear(); throw error(data.error || 'GENERATION_FAILED'); }
          } else if (response.status === 404) {
            missingSince ||= Date.now();
            options.onState?.({stage:accepted ? 'offline' : 'upload_check',createdAt:current.createdAt});
            const payload = !accepted && (inMemoryPayload || await payloadStore('get'));
            if (payload?.body instanceof ArrayBuffer && payload.expiresAt > Date.now() && uploadRetries < 3) {
              uploadRetries++;
              let retried;
              try { retried = await request('', {method:'POST',body:await new Response(payload.body,{headers:{'Content-Type':payload.contentType}}).formData()}); } catch (_) {}
              if (retried?.response.ok) { notify(retried.data); track('wam_yukleme_kurtarildi',{istek_id:current.id}); }
              else if (retried && retried.response.status < 500) { clear(); throw error(retried.data.code); }
            }
            if (Date.now() - missingSince >= 60_000 && !accepted) { clear(); throw error('UPLOAD_NOT_RECEIVED'); }
            if (Date.now() - missingSince >= 60_000 && accepted && !payload) { clear(); throw error('JOB_NOT_FOUND'); }
          } else options.onState?.({ stage: 'offline', createdAt: current.createdAt });
        }
        await new Promise(resolve => {
          const timer = setTimeout(done, document.hidden ? 6000 : 2000);
          function done() { clearTimeout(timer); document.removeEventListener('visibilitychange', done); window.removeEventListener('online', done); resolve(); }
          document.addEventListener('visibilitychange', done, { once: true }); window.addEventListener('online', done, { once: true });
        });
      }
      if (stopped) return null;
      clear(); throw error('JOB_NOT_FOUND');
    }
    return {
      pending: read,
      current: () => current,
      accepted: () => accepted,
      async start(form, meta) {
        if (read()) throw new Error('Your previous preview is still running. Reload this page to open it.');
        const bytes = new Uint8Array(32); crypto.getRandomValues(bytes);
        current = { id: String(form.get('istek_id')), token: Array.from(bytes, n => n.toString(16).padStart(2, '0')).join(''), createdAt: Date.now(), expiresAt: Date.now() + 86400000, accepted: false, meta };
        accepted = false; payloadReleased = false; persist();
        const encoded = new Request(options.api + '/api/generation-jobs', {method:'POST',body:form});
        inMemoryPayload = {id:current.id,body:await encoded.arrayBuffer(),contentType:encoded.headers.get('Content-Type'),expiresAt:current.expiresAt};
        if (!await payloadStore('put', inMemoryPayload)) track('wam_yukleme_depolama_hata', {istek_id:current.id});
        let reply;
        try { reply = await request('', { method: 'POST', body: form }); }
        catch (_) { options.onState?.({ stage: accepted ? 'offline' : 'upload_check', createdAt: current.createdAt }); }
        if (reply) {
          if (!reply.response.ok && reply.response.status < 500) { clear(); throw error(reply.data.code); }
          if (reply.response.ok) { notify(reply.data); track('wam_uretim_kabul', { istek_id: current.id }); }
        }
        return wait(false);
      },
      async resume() { current = read(); if (!current) return null; accepted = current.accepted === true; payloadReleased = false; options.onState?.({stage:accepted ? 'offline' : 'upload_check'}); return wait(true); },
      async subscribe(email, marketing) {
        if (!current || !accepted) throw new Error('Please wait until your photo has uploaded, then try again.');
        const { response, data } = await request('/' + current.id + '/email', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, marketing_consent: marketing }) });
        if (!response.ok || !data.success) throw new Error(messages[data.code] || 'We could not save your email. Please try again.');
        if (['bounced','failed','complained'].includes(data.email_status)) throw new Error('We could not deliver to this address. Check it or enter another email.');
        track('wam_eposta_kabul', { istek_id: current.id, yuzey: 'kalici_is', durum: data.email_status }); return data;
      },
      consume() { clear(); },
      stop() { stopped = true; }
    };
  }
  window.WamGenerationJob = { create };
})();
